/**
 * «Знают или находят» — диагноз к видимости и окно с вердиктами по вопросам.
 *
 * Один и тот же вопрос задан движку дважды: с веб-поиском (недельная перепись) и
 * без него. Из пары ответов «нас не называют» распадается на четыре разных
 * случая, и два из них требуют совершенно разной работы: бренд движку знаком, но
 * его вытесняют свежие страницы конкурентов — это страницы; бренд называют только
 * с поиском — это известность. До этого блока оба выглядели на экране одинаково.
 *
 * Что блок НЕ показывает и почему:
 *
 * * **сводного числа по движкам нет** — его нет и в контракте. Средняя по четырём
 *   движкам прятала бы главное: ChatGPT бренд знает, а Qwen не слышал, и это две
 *   разные работы, а не одно «56%»;
 * * **доли «называют с поиском» нет.** Она приходит в снимке, но на этом же
 *   экране выше уже стоит видимость движка из переписи — то же утверждение на
 *   другом знаменателе. Два похожих процента рядом читаются как противоречие, а
 *   продукт отказывается от двух разных баллов одной величины. Сам факт виден в
 *   окне: у каждого вопроса написано, называет его движок с поиском или нет.
 *
 * Строки таблицы — движки из справочника, а не только те, что пришли в диагнозе:
 * движок, не давший вердикта, обязан быть виден словами «Не измерено». Иначе
 * картина молча улучшается ровно тогда, когда проверка сорвалась.
 */

import { useEffect, useRef, useState } from 'react';

import { requestWith } from '../api/client';
import { Dialog } from './Dialog';
import { engineTitle } from './controls';
import { useRead, type Page } from './query';
import { ErrorNotice, Skeleton } from './state';
import { MEMORY_VERDICT_ORDER, memoryVerdict, memoryVerdictNote } from './labels';
import { t } from '../i18n';
import { coverage, dateProse, int, percent } from '@shared/src/format';
import type { components } from '../api/schema';

type MemoryDiagnosis = components['schemas']['MemoryDiagnosis'];
type MemorySnapshot = components['schemas']['MemorySnapshot'];
type MemoryQuestion = components['schemas']['MemoryQuestion'];
type MemoryVerdict = components['schemas']['MemoryVerdict'];
type Engine = components['schemas']['Engine'];
type Run = components['schemas']['Run'];

/** Путь ручки разреза по вопросам — он же ключ доступа роли. */
export const MEMORY_QUESTIONS_PATH = '/brands/{brandId}/metrics/memory/questions';

/** Сколько вердиктов берём за раз. Дальше — по кнопке, курсором контракта. */
const PAGE = 25;

/**
 * Движки блока: подключённые, у которых поиск выключается, плюс те, что пришли в
 * диагнозе.
 *
 * Объединение, а не только справочник: движок, отключённый после переписи, свой
 * вердикт уже отдал, и терять его строку значит переписывать прошлое. Порядок —
 * справочника, чтобы движки на всех экранах стояли в одном порядке.
 */
function codesOf(
  catalogue: Engine[],
  rows: MemorySnapshot[],
  order: (a: string, b: string) => number,
): string[] {
  const probing = catalogue
    .filter((engine) => engine.is_active && engine.memory_probe)
    .map((engine) => engine.code);
  const seen = new Set([...probing, ...rows.map((row) => row.engine_code)]);
  return [...seen].sort(order);
}

export function MemoryBlock({
  brandId,
  memory,
  catalogue,
  titles,
  order,
  canOpenQuestions,
}: {
  brandId: string;
  /** `null` — диагноза за эту дату нет вовсе: проверка ещё не проходила. */
  memory: MemoryDiagnosis | null | undefined;
  catalogue: Engine[];
  titles: Map<string, string>;
  order: (a: string, b: string) => number;
  /** Разрез по вопросам печатает ядро, и клиенту агентства он закрыт. */
  canOpenQuestions: boolean;
}) {
  const [open, setOpen] = useState(false);

  const diagnosis = memory ?? null;
  const rows = diagnosis?.engines ?? [];
  const codes = codesOf(catalogue, rows, order);

  // Проверять нечего: ни одного подключённого движка, у которого поиск
  // выключается. У Perplexity, Нейро и «Обзора от Google» он встроен, ответа без
  // поиска у них не существует — и обещать диагноз, которого не будет, нельзя.
  if (codes.length === 0) return null;

  const probe = diagnosis?.active_probe ?? null;

  // Диагноза нет вовсе либо он пуст: это «ещё не спрашивали», а не ноль.
  if (!diagnosis || rows.length === 0) {
    return (
      <section className="block">
        <h2>{t('memory.title')}</h2>
        <p className="lede">
          {probe ? t('memory.lede.pending-probe') : t('memory.lede.pending')}
        </p>
        {/* Пока проверки не было, состояние называется словом продукта — «Не
            измерено», — а не обещанием. Состав движков остаётся: он приходит из
            справочника и говорит, у кого поиск вообще выключается. */}
        <p className="stamp">
          {probe
            ? probeProse(probe)
            : t('memory.probe-off', { engines: enginesProse(codes, titles) })}
        </p>
      </section>
    );
  }

  const byCode = new Map(rows.map((row) => [row.engine_code, row]));

  // Охват считается по одному множеству с обеих сторон дроби — по тому самому, по
  // которому нарисованы столбцы: подключённые сегодня движки с выключаемым
  // поиском.
  //
  // Раньше числителем стояло число строк диагноза, а целым — `engines_expected`, и
  // это две разные мерки. Движок, отключённый уже после замера, свой вердикт отдал
  // и остался столбцом — на такой паре печаталось «вердикты по 4 из 3 движков»,
  // охват больше целого. Обратный перекос так же врёт: справочник свежее диагноза,
  // движок подключили после замера, столбец «Не измерено» в таблице стоит, а
  // подпись рапортует «3 из 3».
  //
  // Поэтому целое — состав справочника, а `engines_expected` остаётся запасом на
  // те кадры, пока справочник ещё не пришёл. Движки, выпавшие из состава,
  // молчанием не заметаются: они названы под таблицей отдельной оговоркой.
  //
  // Признак загрузки — сам справочник, а не число проверяемых в нём движков. Ноль
  // проверяемых при загруженном справочнике — законный состав (проверку памяти
  // сняли со всех движков после этого замера), и путать его с «ещё не приехало»
  // нельзя: на такой паре подпись печатала «вердикты по 2 из 0 движков», а
  // отключённые движки не попадали в оговорку.
  const loaded = catalogue.length > 0;
  const expectedCodes = new Set(
    catalogue
      .filter((engine) => engine.is_active && engine.memory_probe)
      .map((e) => e.code),
  );
  const whole = loaded ? expectedCodes.size : diagnosis.engines_expected;
  const live = loaded ? rows.filter((row) => expectedCodes.has(row.engine_code)) : rows;
  const counted = live.filter(measured).length;
  const retired = loaded
    ? codes.filter((code) => byCode.has(code) && !expectedCodes.has(code))
    : [];

  return (
    <section className="block">
      <h2>{t('memory.title')}</h2>
      <p className="lede">{t('memory.lede')}</p>

      <div className="tbl-wrap">
        <table className="mini diag">
          {/* Основание таблицы: что сравнивалось и под каким замером лежит
              диагноз. Дата — замера, а не дня проверки: вердикт объясняет ту
              самую точку, числа которой стоят выше. */}
          <caption>
            {t('memory.caption', { date: dateProse(diagnosis.snapshot_date) })}
          </caption>
          <thead>
            <tr>
              <th scope="col">{t('memory.head')}</th>
              {codes.map((code) => (
                <th scope="col" className="num" key={code}>
                  {engineTitle(titles, code)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">
                {t('memory.row.share')}
                <span>{t('memory.row.share-sub')}</span>
              </th>
              {codes.map((code) => {
                const share = byCode.get(code)?.memory_share;
                return (
                  <td className="tnum num" key={code}>
                    {share === null || share === undefined
                      ? t('not-measured')
                      : percent(share, 1)}
                  </td>
                );
              })}
            </tr>

            {MEMORY_VERDICT_ORDER.map((verdict) => (
              <tr key={verdict}>
                <th scope="row">
                  {memoryVerdict(verdict)}
                  <span>{memoryVerdictNote(verdict)}</span>
                </th>
                {codes.map((code) => {
                  const row = byCode.get(code);
                  return (
                    <td className="tnum num" key={code}>
                      {row && measured(row)
                        ? int(countIn(row, verdict))
                        : t('not-measured')}
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr>
              <th scope="row">
                {t('memory.row.compared')}
                <span>{t('memory.row.compared-sub')}</span>
              </th>
              {/* Единственная строка, где ноль — настоящее число: «0 из 24»
                  говорит, что сетка была запланирована, а сравнить не удалось
                  ничего. Выше в такой строке стоит «Не измерено»: четыре нуля по
                  вердиктам читались бы как измеренный результат. */}
              {codes.map((code) => {
                const row = byCode.get(code);
                return (
                  <td className="tnum num" key={code}>
                    {row
                      ? coverage(row.questions_counted, row.questions_planned)
                      : t('not-measured')}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="tbl-foot">
        <p className="stamp">
          {/* Сколько ожидаемых сегодня движков дали вердикт. Пришло меньше — это
              не полная картина, и говорится это здесь, а не в журнале. Снимок без
              единого сравнения вердиктом не считается, а движок, отключённый после
              замера, в дробь не входит и назван отдельной оговоркой: его столбец в
              таблице стоит, а ждать его сегодня уже незачем.

              Падеж после «из» живёт в самой ICU-строке.

              Неполнота бывает двух разных родов, и обещание «доли ещё сдвинутся»
              верно только для одного. Пропавший движок чужие доли не меняет: у
              оставшихся сетка закрыта, их числа окончательные. Сдвинутся они
              тогда, когда у САМОГО движка сравнено меньше запланированного, — и
              сказано это ровно в этом случае. Спрашивается это у движков, которых
              ждут сегодня: у отключённого сетка не закроется никогда, и обещать
              по нему сдвиг нельзя.

              Ждать сегодня может быть не от кого: проверку памяти сняли со всех
              движков уже после этого замера. Дроби тогда нет — «0 из 0 движков»
              выглядит поломкой, — и сказано прямо, что новых вердиктов не будет, а
              снятые остаются в таблице под своей датой. */}
          {whole === 0
            ? t('memory.foot.none')
            : t('memory.foot.verdicts', { counted, whole })}
          {retired.length > 0
            ? t('memory.foot.retired', { engines: enginesProse(retired, titles) })
            : ''}
          {live.some((row) => !row.is_complete)
            ? t('memory.foot.moving')
            : diagnosis.is_complete
              ? ''
              : t('memory.foot.incomplete')}
          {probe ? ` · ${probeProse(probe)}` : ''}
        </p>
        {canOpenQuestions ? (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={() => setOpen(true)}
          >
            {t('memory.open-questions')}
          </button>
        ) : null}
      </div>

      {open ? (
        <WhyDialog
          brandId={brandId}
          date={diagnosis.snapshot_date}
          codes={codes}
          rows={rows}
          titles={titles}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}

/**
 * Есть ли в снимке хоть одно сравнение.
 *
 * Контракт разрешает снимок, в котором сравнено ноль вопросов: движок в проверке
 * участвовал, но ни одной пары ответов не сложилось (доли тогда пустые). Четыре
 * нуля по вердиктам в такой строке — это «не знают и не называют» на пустом
 * замере, то есть отсутствие измерения, выданное за измеренный ноль. Такой снимок
 * стоит «Не измерено» и в охват не идёт.
 */
function measured(row: MemorySnapshot): boolean {
  return row.questions_counted > 0;
}

/** Счётчик одного вердикта в снимке движка. */
function countIn(row: MemorySnapshot, verdict: MemoryVerdict): number {
  if (verdict === 'known_and_found') return row.known_and_found;
  if (verdict === 'known_not_found') return row.known_not_found;
  if (verdict === 'found_via_search') return row.found_via_search;
  return row.absent;
}

/**
 * Идущая проверка: своими числами и без полосы прогресса.
 *
 * Полоса обещала бы, что видимость сейчас пересчитается, а проверка её не меняет:
 * она меряет причину, а не число. Поэтому строка, а не прибор.
 */
function probeProse(probe: Run): string {
  const planned = probe.planned_tasks ?? 0;
  const done = probe.done_tasks ?? 0;
  if (probe.state === 'queued' || planned === 0) return t('memory.probe.queued');
  return t('memory.probe.running', { done, planned });
}

/** Перечисление движков словами: «ChatGPT, Gemini и DeepSeek». */
function enginesProse(codes: string[], titles: Map<string, string>): string {
  const names = codes.map((code) => engineTitle(titles, code));
  if (names.length < 2) return names.join('');
  return `${names.slice(0, -1).join(', ')}${t('list.and')}${names[names.length - 1]}`;
}

/* ─────────────────────── окно: вердикты по вопросам ───────────────────────── */

/** `all` — фильтр снят. Остальное — код движка из справочника. */
type EngineFilter = string;
type VerdictFilter = 'all' | MemoryVerdict;

/**
 * Разрез по вопросам.
 *
 * Диагноз без него остаётся долей на плитке, а работать можно с вопросами, а не
 * с процентом. Оба ответа, из которых вердикт получен, здесь не показываются:
 * ленту ответов зонд не наполняет (его ответ говорит о памяти движка, а не о
 * выдаче), и обещать кнопку «посмотреть оба ответа» значило бы обещать пустоту.
 */
function WhyDialog({
  brandId,
  date,
  codes,
  rows,
  titles,
  onClose,
}: {
  brandId: string;
  date: string;
  codes: string[];
  rows: MemorySnapshot[];
  titles: Map<string, string>;
  onClose: () => void;
}) {
  const [engine, setEngine] = useState<EngineFilter>('all');
  const [verdict, setVerdict] = useState<VerdictFilter>('all');

  const path = `/brands/${brandId}/metrics/memory/questions`;
  const filter = {
    date,
    ...(engine === 'all' ? {} : { engine_code: engine }),
    ...(verdict === 'all' ? {} : { verdict }),
  };
  const key = `${engine}|${verdict}`;
  const first = useRead<Page<MemoryQuestion>>(path, { ...filter, limit: PAGE });

  // Дочитанные страницы держатся вместе с набором фильтров, под которым они
  // получены: смена фильтра — это другой список, а не продолжение этого.
  // Состояние сверяется при отрисовке, а не выправляется эффектом, — иначе кадр
  // показывает вопросы прошлого фильтра под новой подписью.
  const [tail, setTail] = useState<{
    of: string;
    items: MemoryQuestion[];
    cursor: string | null;
    busy: boolean;
    error: unknown;
  }>({ of: key, items: [], cursor: null, busy: false, error: null });
  const extra =
    tail.of === key ? tail : { items: [], cursor: null, busy: false, error: null };

  // Дочитывание не переживает окно: ответ, пришедший после закрытия, ложится в
  // состояние, которого уже никто не покажет.
  const reading = useRef<AbortController | null>(null);
  useEffect(() => () => reading.current?.abort(), []);

  const page = first.kind === 'ready' ? first.data : null;
  const items = page ? [...page.items, ...extra.items] : [];
  const cursor = extra.items.length > 0 ? extra.cursor : (page?.next_cursor ?? null);

  const more = () => {
    if (!cursor) return;
    const controller = new AbortController();
    reading.current?.abort();
    reading.current = controller;
    setTail({ ...extra, of: key, busy: true, error: null });
    // Читаем напрямую: `useRead` держит одну страницу, а докрутка их копит.
    requestWith<Page<MemoryQuestion>>(path, {
      query: { ...filter, limit: PAGE, cursor },
      signal: controller.signal,
    })
      .then(({ body }) =>
        setTail((current) => ({
          of: key,
          items: [...(current.of === key ? current.items : []), ...body.items],
          cursor: body.next_cursor,
          busy: false,
          error: null,
        })),
      )
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setTail((current) => ({ ...current, of: key, busy: false, error }));
      });
  };

  const total = totalOf(rows, engine, verdict);

  return (
    <Dialog
      open
      title={t('memory.dialog.title')}
      onClose={onClose}
      wide
      // Выход мышью обязателен: закрытия кликом по подложке в этом окне нет
      // намеренно, и без кнопки у окна оставался бы один выход — Escape.
      footer={
        <button className="btn btn-ghost" type="button" onClick={onClose}>
          {t('cell.close')}
        </button>
      }
    >
      <p className="dlg-lead">{t('memory.dialog.lead')}</p>

      <div className="filters">
        <div className="field inline">
          <label htmlFor="mem-engine">{t('memory.filter.engine')}</label>
          <select
            id="mem-engine"
            value={engine}
            onChange={(event) => setEngine(event.target.value)}
          >
            <option value="all">{t('memory.filter.all-engines')}</option>
            {codes.map((code) => (
              <option value={code} key={code}>
                {engineTitle(titles, code)}
              </option>
            ))}
          </select>
        </div>
        <div className="field inline">
          <label htmlFor="mem-verdict">{t('memory.filter.verdict')}</label>
          <select
            id="mem-verdict"
            value={verdict}
            onChange={(event) => setVerdict(event.target.value as VerdictFilter)}
          >
            <option value="all">{t('memory.filter.all-verdicts')}</option>
            {MEMORY_VERDICT_ORDER.map((value) => (
              <option value={value} key={value}>
                {memoryVerdict(value)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {first.kind === 'loading' ? <Skeleton rows={3} /> : null}
      {first.kind === 'failed' ? <ErrorNotice error={first.error} /> : null}

      {/* Пусто — это состояние фильтра, а не пустой экран: заголовка и кнопки
          здесь не нужно, окно уже названо. Причин пустоты две, и для человека они
          разные: у движка сравнённых вопросов нет вовсе — или под вердикт не попал
          ни один вопрос. Одна фраза на оба случая выдавала бы наш пробел за факт, а
          предложение снять фильтр вело бы к такой же пустоте: снимать нечего.

          Снимок без единого сравнения читается тут так же, как отсутствие снимка:
          движок в проверке был, а ни одной пары ответов не сложилось, и вопросов
          под ним нет ни под одним вердиктом. */}
      {first.kind === 'ready' && items.length === 0 ? (
        <p className="stamp">
          {engine !== 'all' &&
          !rows.some((row) => row.engine_code === engine && measured(row))
            ? t('memory.empty.engine', { engine: engineTitle(titles, engine) })
            : t('memory.empty.verdict')}
        </p>
      ) : null}

      {items.length > 0 ? (
        <>
          <div className="tbl-wrap">
            <table className="mini">
              <caption>
                {t('memory.table.caption', { date: dateProse(date) })}
                {engine === 'all' ? '' : ` · ${engineTitle(titles, engine)}`}
                {verdict === 'all' ? '' : ` · ${memoryVerdict(verdict)}`}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('memory.col.question')}</th>
                  <th scope="col">{t('memory.col.engine')}</th>
                  <th scope="col">{t('memory.col.verdict')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={`${item.question_id}-${item.engine_code}`}>
                    <td>{item.question_text}</td>
                    <td>{engineTitle(titles, item.engine_code)}</td>
                    <td>{memoryVerdict(item.verdict)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tbl-foot">
            <p className="stamp">
              {/* Показано из скольких — счёт строк, а не показатель: сколько
                  сравнений сошлось, сказано в самом диагнозе, и здесь берётся
                  оттуда же. Страница без итога прячет то, чего человек не
                  посмотрел (app-kit.md §3). */}
              {t('memory.shown', { shown: items.length, total })}
            </p>
            {cursor ? (
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={more}
                disabled={extra.busy}
                aria-busy={extra.busy}
              >
                {extra.busy ? t('memory.reading') : t('memory.more')}
              </button>
            ) : null}
          </div>

          {extra.error ? <ErrorNotice error={extra.error} /> : null}
        </>
      ) : null}
    </Dialog>
  );
}

/**
 * Сколько сравнений всего под выбранными фильтрами.
 *
 * Берётся из того же диагноза, что стоит в таблице блока: счётчики вердиктов уже
 * посчитаны сведением и лежат в снимке. Складываются здесь строки, а не
 * показатель, — доли по-прежнему приходят готовыми.
 */
function totalOf(
  rows: MemorySnapshot[],
  engine: EngineFilter,
  verdict: VerdictFilter,
): number {
  const picked =
    engine === 'all' ? rows : rows.filter((row) => row.engine_code === engine);
  return picked.reduce(
    (sum, row) =>
      sum + (verdict === 'all' ? row.questions_counted : countIn(row, verdict)),
    0,
  );
}
